/**
 * The go-live import's CSV handling: a small RFC 4180 reader (quoted fields,
 * commas and line breaks inside quotes, doubled quotes, CRLF or LF), and the
 * template the import screen hands out. Pure functions, no library.
 */

/** Every column the import understands, in template order. */
export const IMPORT_COLUMNS = [
  'nameEn',
  'nameUr',
  'fatherName',
  'cnic',
  'contactNo',
  'farmCode',
  'department',
  'designation',
  'workerType',
  'payFrequency',
  'joinedOn',
  'basicPay',
  'houseRent',
  'conveyance',
  'medical',
  'dailyRate',
  'paymentMethod',
  'payBankName',
  'payAccountTitle',
  'payIban',
  'walletProvider',
  'walletNo',
  'eobiNo',
  'pessiNo',
  'taxApplies',
  'taxableSoFar',
  'taxSoFar',
  'loanOwed',
  'loanInstalment',
  'advanceOwed',
  'casualLeaveLeft',
  'sickLeaveLeft',
  'annualLeaveLeft',
] as const;

export type ImportColumn = (typeof IMPORT_COLUMNS)[number];

/** Columns every file must have, even when a row leaves them empty. */
export const REQUIRED_COLUMNS: ImportColumn[] = ['nameEn', 'workerType', 'joinedOn'];

/**
 * Reads CSV text into rows of cells. Follows RFC 4180: a field in double
 * quotes may hold commas, line breaks and "" for a quote. A byte-order mark
 * at the start (Excel adds one) is dropped. A final line break does not make
 * an extra empty row.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = text.charCodeAt(0) === 0xfeff ? 1 : 0;

  for (; i < text.length; i++) {
    const c = text[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"' && field === '') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\r' || c === '\n') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** One cell, quoted only when it has to be. */
export function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

/** The empty template: just the header row. */
export function importTemplate(): string {
  return toCsv([[...IMPORT_COLUMNS]]);
}

export interface ImportSheet {
  /** Data rows, with the line of the file each came from (the header is line 1). */
  rows: Array<{ line: number; values: Partial<Record<ImportColumn, string>> }>;
  /** Header cells the import does not know; their values are ignored. */
  unknown: string[];
  /** Required columns the header does not have. */
  missing: ImportColumn[];
}

/**
 * Turns a CSV file into import rows keyed by column name. Header names match
 * without regard to case or surrounding spaces; blank lines are skipped;
 * cells are trimmed and empty cells left out.
 */
export function readImportSheet(text: string): ImportSheet {
  const table = parseCsv(text);
  const header = (table[0] ?? []).map((h) => h.trim());
  const byLower = new Map<string, ImportColumn>(IMPORT_COLUMNS.map((c) => [c.toLowerCase(), c]));
  const keys = header.map((h) => byLower.get(h.toLowerCase()) ?? null);
  const unknown = header.filter((h, i) => h !== '' && keys[i] === null);
  const missing = REQUIRED_COLUMNS.filter((c) => !keys.includes(c));

  const rows: ImportSheet['rows'] = [];
  table.slice(1).forEach((cells, index) => {
    if (cells.every((c) => c.trim() === '')) return;
    const values: Partial<Record<ImportColumn, string>> = {};
    cells.forEach((cell, i) => {
      const key = keys[i];
      const v = cell.trim();
      if (key && v !== '') values[key] = v;
    });
    rows.push({ line: index + 2, values });
  });
  return { rows, unknown, missing };
}
